// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The phoneme table, and what a callout's phoneme string means (M3-27, 07 §4).
//
// The data lives in `contracts/pinball/v1/audio/phonemes.json` because 07 §4 puts it there, and
// the reason is parity: "a shared phoneme table lives in contracts/… so both cores render the
// same voice". A table compiled into the TypeScript client would be a table Unity has to be
// given a copy of, and two copies of a hundred numbers is two copies that drift. This module is
// the typed reading of that file, not a second copy of it.
//
// **Callouts are authored as phonemes, not as text.** 07 §4 is explicit about why: it "keeps
// quality under the designer's control and removes any dependence on a text-to-speech engine,
// which would differ between browsers and Unity and break parity". So there is no grapheme
// layer here and there is not going to be one. What this module does is turn a string of
// phoneme names into segments with durations and syllables, which is everything the synthesizer
// needs and nothing it does not.

import table from '../../contracts/pinball/v1/audio/phonemes.json'

/** How a phoneme is made, which decides how the synthesizer treats it. */
export type PhonemeClass =
  | 'vowel'
  | 'diphthong'
  | 'liquid'
  | 'glide'
  | 'nasal'
  | 'fricative'
  | 'aspirate'
  | 'stop'
  | 'affricate'

/** One resonance of the vocal tract: where it is, and how sharp. */
export interface Formant {
  readonly hz: number
  readonly bandwidthHz: number
}

/**
 * One phoneme's target.
 *
 * `voiced` and `noise` are the source-filter model's whole vocabulary — how much glottal pulse
 * train and how much noise the segment is made of. A voiced fricative is not a third kind of
 * source; it is a segment with some of each, which is also what a larynx is doing.
 */
export interface Phoneme {
  readonly name: string
  readonly cls: PhonemeClass
  /** Nominal duration before prosody scales it, milliseconds. */
  readonly ms: number
  readonly voiced: number
  readonly noise: number
  /** Overall amplitude. Fricatives are much quieter than vowels and the table says how much. */
  readonly level: number
  readonly formants: readonly Formant[]
  /** Where the formants have moved to by the end of the segment. Diphthongs only. */
  readonly glide: readonly Formant[] | undefined
  readonly gains: readonly number[]
  /** Silence before a plosive is released. Zero for everything that is not a stop. */
  readonly closureMs: number
  /** How long the release lasts. Zero for everything that is not a stop or an affricate. */
  readonly burstMs: number
}

/** Formants per phoneme. Five, per 07 §4 — F1..F5. */
export const FORMANT_COUNT = 5

/** How long a formant takes to travel from one phoneme's target to the next (07 §4). */
export const TRANSITION_MS = table.reference.transitionMs

/** The table, by ARPAbet name. */
export const PHONEMES: ReadonlyMap<string, Phoneme> = readTable()

function readTable(): Map<string, Phoneme> {
  const out = new Map<string, Phoneme>()
  const raw = table.phonemes as Readonly<Record<string, RawPhoneme>>
  for (const name of Object.keys(raw)) {
    const entry = raw[name]!
    out.set(name, {
      name,
      cls: entry.class as PhonemeClass,
      ms: entry.ms,
      voiced: entry.voiced,
      noise: entry.noise,
      // A vowel has no `level`: it is the reference the rest of the table is quiet against.
      level: entry.level ?? 1,
      formants: entry.formants.map(readFormant),
      glide: entry.glide?.map(readFormant),
      gains: entry.gains,
      closureMs: entry.closureMs ?? 0,
      burstMs: entry.burstMs ?? 0,
    })
  }
  return out
}

interface RawPhoneme {
  readonly class: string
  readonly ms: number
  readonly voiced: number
  readonly noise: number
  readonly level?: number
  readonly formants: readonly (readonly number[])[]
  readonly glide?: readonly (readonly number[])[]
  readonly gains: readonly number[]
  readonly closureMs?: number
  readonly burstMs?: number
}

function readFormant(pair: readonly number[]): Formant {
  return { hz: pair[0] ?? 0, bandwidthHz: pair[1] ?? 100 }
}

/**
 * Names in a phoneme string that the table has never heard of.
 *
 * The same report `silentCues` gives for the cue bank, for the same reason: a callout with a
 * typo in it still loads, still fires, and says a slightly different word — or, if the typo is
 * on the only vowel, says nothing at all. Neither failure raises anything by itself.
 */
export function unknownPhonemes(sequence: string): string[] {
  const bad: string[] = []
  for (const name of sequence.split(/\s+/)) {
    if (name.length === 0 || PHONEMES.has(name)) continue
    if (!bad.includes(name)) bad.push(name)
  }
  return bad
}

/** A phoneme string as the synthesizer wants it: known phonemes, in order. */
export function parsePhonemes(sequence: string): Phoneme[] {
  const out: Phoneme[] = []
  for (const name of sequence.split(/\s+/)) {
    const phoneme = PHONEMES.get(name)
    if (phoneme !== undefined) out.push(phoneme)
  }
  return out
}

/** Whether a phoneme can be the nucleus of a syllable. */
export function isNucleus(phoneme: Phoneme): boolean {
  return phoneme.cls === 'vowel' || phoneme.cls === 'diphthong'
}

/**
 * Group phonemes into syllables, so `emphasis` indices mean something.
 *
 * A machine definition marks emphasis by **syllable**, which is the unit a person hears stress
 * on — `"novaMultiball"` marks 0 and 2 and means NO-va MUL-ti-ball. So the string has to be
 * divided the way a speaker would divide it, and the rule that does that is the maximal-onset
 * principle: of the consonants between two vowels, as many as can start a syllable do. Applied
 * to one consonant, which is where nearly all of the value is: `s t aa r k ao r` splits as
 * `star | cor` rather than `stark | or`, and a stress mark on syllable 1 lands where the author
 * meant it.
 *
 * Returns one array of indices into `phonemes` per syllable. A sequence with no vowel in it is
 * one syllable, because the alternative is zero and then nothing can be stressed.
 */
export function syllables(phonemes: readonly Phoneme[]): number[][] {
  const nuclei: number[] = []
  for (let i = 0; i < phonemes.length; i++) if (isNucleus(phonemes[i]!)) nuclei.push(i)
  if (nuclei.length === 0) return phonemes.length > 0 ? [phonemes.map((_, i) => i)] : []

  const out: number[][] = []
  for (let s = 0; s < nuclei.length; s++) {
    // Everything up to the first nucleus is its onset; after the last, its coda.
    const from = s === 0 ? 0 : boundary(nuclei[s - 1]!, nuclei[s]!)
    const to = s === nuclei.length - 1 ? phonemes.length : boundary(nuclei[s]!, nuclei[s + 1]!)
    const group: number[] = []
    for (let i = from; i < to; i++) group.push(i)
    out.push(group)
  }
  return out
}

/** Where the syllable break falls between two nuclei: one consonant onto the following onset. */
function boundary(previousNucleus: number, nextNucleus: number): number {
  const between = nextNucleus - previousNucleus - 1
  if (between <= 0) return nextNucleus
  return nextNucleus - 1
}
